import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { SavedScreener } from '../../../types/screener.type';
import { PRESET_STRATEGIES } from '../../../constants/screener/presets.constant';
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/arrow-down.svg';
import { ReactComponent as ArrowLeftIcon } from '../../../assets/icons/arrow-left-s-line.svg';
import { ReactComponent as CloseCircleFillIcon } from '../../../assets/icons/close-circle-fill.svg';
import './ScreenerDropdown.css';

// Define the props interface for the Dropdown component
interface ScreenerDropdownProps {
  isSmallScreen: boolean;
  screenerName: string;
  setCurrentScreenerId: (currentScreenerId: string) => void;
  currentPresetId: string | null;
  onSelectPreset: (presetId: string) => void;
  selectedScreenerId: string | null;
  screeners: SavedScreener[];
  onRequestDelete: (id: string) => void;
}

// Dropdown component
const ScreenerDropdown = ({
  isSmallScreen,
  screenerName,
  setCurrentScreenerId,
  currentPresetId,
  onSelectPreset,
  selectedScreenerId,
  screeners,
  onRequestDelete,
}: ScreenerDropdownProps) => {
  // State to manage whether the dropdown list is open or closed
  const [isOpen, setIsOpen] = useState(false);

  // Ref to detect clicks outside the dropdown to close it
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Effect to handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // On mobile the list is a full-screen overlay - there's no "outside"
      // to click, closing is handled by the back arrow / Cancel button.
      if (isSmallScreen) return;
      // If the click is outside the dropdown component, close it
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // Add event listener for clicks
    document.addEventListener('mousedown', handleClickOutside);
    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSmallScreen]);

  // Function to toggle the dropdown's open/closed state
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSavedScreenerClick = (id: string) => {
    setCurrentScreenerId(id);
    setIsOpen(false);
  };

  const handlePresetClick = (presetId: string) => {
    onSelectPreset(presetId);
    setIsOpen(false);
  };

  const renderSavedScreeners = () => {
    if (screeners.length === 0) {
      return (
        <Typography className="screener-dropdown__empty-text">
          No saved strategies yet
        </Typography>
      );
    }

    return screeners.map((screener) => (
      <Box
        key={screener.id}
        className={`screener-dropdown__item${
          screener.id === selectedScreenerId
            ? ' screener-dropdown__item--active'
            : ''
        }`}
      >
        <Button
          className="screener-dropdown__item-btn"
          onClick={() => handleSavedScreenerClick(screener.id)}
        >
          <Typography className="screener-dropdown__item-text">
            {screener.name}
          </Typography>
        </Button>
        <Button
          className={`screener-dropdown__delete-btn${
            isSmallScreen ? ' screener-dropdown__delete-btn--visible' : ''
          }`}
          onClick={() => onRequestDelete(screener.id)}
        >
          <CloseCircleFillIcon className="screener-dropdown__delete-icon" />
        </Button>
      </Box>
    ));
  };

  const renderPresetStrategies = () => {
    return PRESET_STRATEGIES.map((preset) => (
      <Box
        key={preset.id}
        className={`screener-dropdown__item${
          preset.id === currentPresetId ? ' screener-dropdown__item--active' : ''
        }`}
      >
        <Button
          className="screener-dropdown__item-btn"
          onClick={() => handlePresetClick(preset.id)}
        >
          <Typography className="screener-dropdown__item-text">
            {preset.name}
          </Typography>
        </Button>
      </Box>
    ));
  };

  const renderSectionsContent = () => (
    <>
      <Box className="screener-dropdown__header">
        <Typography
          variant="body2"
          className="screener-dropdown__header-title"
        >
          Your saved strategies
        </Typography>
      </Box>
      <Box className="screener-dropdown__saved-list">
        {renderSavedScreeners()}
      </Box>

      <Box className="screener-dropdown__header screener-dropdown__header--presets">
        <Typography
          variant="body2"
          className="screener-dropdown__header-title"
        >
          Preset strategies
        </Typography>
      </Box>
      <Box className="screener-dropdown__preset-list">
        {renderPresetStrategies()}
      </Box>
    </>
  );

  const renderDropdownContent = () => {
    return (
      <Box className="screener-dropdown__content">{renderSectionsContent()}</Box>
    );
  };

  const renderMobileFullScreen = () => {
    return (
      <Box className="screener-dropdown__mobile-overlay">
        <Box className="screener-dropdown__mobile-header">
          <IconButton
            className="screener-dropdown__mobile-back"
            aria-label="Back"
            onClick={handleToggle}
          >
            <ArrowLeftIcon />
          </IconButton>
          <Typography className="screener-dropdown__mobile-title">
            Saved strategies
          </Typography>
        </Box>
        <Box className="screener-dropdown__mobile-body">
          {renderSectionsContent()}
        </Box>
        <Box className="screener-dropdown__mobile-footer">
          <Button
            className="screener-dropdown__mobile-cancel"
            onClick={handleToggle}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box className="screener-dropdown" ref={dropdownRef}>
      <Button
        onClick={handleToggle}
        className={`screener-dropdown__toggle ${
          isSmallScreen
            ? 'screener-dropdown__toggle--small'
            : 'screener-dropdown__toggle--large'
        }`}
      >
        <Typography
          variant="body2"
          className={`screener-dropdown__toggle-text${
            screenerName ? '' : ' screener-dropdown__toggle-text--placeholder'
          }`}
        >
          {screenerName || 'Select saved strategy'}
        </Typography>
        <ArrowDownIcon
          className={`screener-dropdown__arrow ${
            isOpen ? 'screener-dropdown__arrow--open' : ''
          }`}
        />
      </Button>
      {isOpen &&
        (isSmallScreen ? renderMobileFullScreen() : renderDropdownContent())}
    </Box>
  );
};

export default ScreenerDropdown;
